import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Zoom,
  useMediaQuery,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  VpnKey as KeyIcon,
} from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import GridOnIcon from "@mui/icons-material/GridOn";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BRANCH_URL = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;
const EMPTY_BRANCH = {
  name: "",
  username: "",
  password: "",
  gateway: "",
  ripIP: "",
  cpeIP: "",
  status: "Active",
  serviceNumber: "",
  primaryContact: "",
  secondaryContact: "",
};

// ===== WHITE THEME WITH YELLOW ACCENTS =====
const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const lightStyles = {
  container: { minHeight: "100vh", bgcolor: "#f5f5f5", py: 4 },
  paper: {
    bgcolor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  dialogPaper: {
    bgcolor: "#ffffff",
    border: "1px solid #FFD600",
    color: "#000000",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
    "& .MuiSelect-icon": { color: "#FFD600" },
  },
  tableHeader: {
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #FFD600",
    backgroundColor: "#f5f5f5",
  },
  tableCell: {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0",
  },
  actionBtn: {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 0 15px #FFD600",
    },
  },
  yellowBorder: {
    borderColor: "#FFD600",
    "&:hover": {
      borderColor: "#ffcc00",
      backgroundColor: "rgba(255, 214, 0, 0.1)",
    },
  },
};

const BranchCreation = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // State
  const filterBarRef = useRef(null);
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState(EMPTY_BRANCH);
  const [editIndex, setEditIndex] = useState(null);
  const [branchSearch, setBranchSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Add this with your other state declarations
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Dialog Controls
  const [formOpen, setFormOpen] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, message: msg, severity });

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BRANCH_URL}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showSnackbar("Registry Sync Failed", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleBranchChange = (e) =>
    setNewBranch({ ...newBranch, [e.target.name]: e.target.value });

  const saveBranch = async () => {
    // 1. Validation
    if (!newBranch.name || !newBranch.username || !newBranch.serviceNumber) {
      return showSnackbar("Required fields missing", "error");
    }

    try {
      // 2. Logic: If editIndex holds an ID, we UPDATE. Otherwise, we CREATE.
      if (editIndex !== null) {
        // editIndex now contains the actual _id string (e.g., "65e8...")
        const res = await axios.put(`${BRANCH_URL}/${editIndex}`, newBranch, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar(res.data.msg || "Node Updated");
      } else {
        const res = await axios.post(`${BRANCH_URL}/create`, newBranch, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar(res.data.msg || "Node Initialized");
      }

      // 3. Reset State & Refresh
      setFormOpen(false);
      setEditIndex(null);
      setNewBranch(EMPTY_BRANCH);
      fetchBranches();
    } catch (err) {
      // 4. Requirement: Show specific server error (e.g., "Username exists")
      const serverMsg = err.response?.data?.msg || "Operation Failed";
      showSnackbar(serverMsg, "error");

      // Requirement: Logout on session expiry
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  // ✅ FIX: Use ID and Username, not index
  const deleteBranch = async (id, username) => {
    if (!window.confirm(`Purge node ${username}?`)) return;

    try {
      const res = await axios.delete(`${BRANCH_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSnackbar(res.data.msg, "warning"); // Show message sent from server
      fetchBranches();
    } catch (err) {
      const serverMsg = err.response?.data?.msg || "Purge Failed";
      showSnackbar(serverMsg, "error");

      // ✅ Requirement: Logout on unauthorized
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const downloadBranchesCSV = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${BRANCH_URL}/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to download CSV");

      const blob = await response.blob(); // Get the CSV as a blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Branches.xlsx"; // File name
      link.click();
      window.URL.revokeObjectURL(url); // Cleanup
    } catch (err) {
      console.error(err);
      alert("Error downloading CSV");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoadingImport(true);
    showSnackbar("INITIALIZING_IMPORT...", "info");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${BRANCH_URL}/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Extracting your specific backend response fields
      const { msg, branchesInserted, usersCreated, skipped } = res.data;

      // Constructing a detailed success message
      const summary = `${msg} | Added: ${branchesInserted} | Users: ${usersCreated} | Skipped: ${skipped}`;

      showSnackbar(summary, "success");
      fetchBranches(); // Refresh the list to show new data
    } catch (err) {
      const errorMsg = err.response?.data?.msg || "IMPORT_PROTOCOL_ERROR";
      showSnackbar(errorMsg, "error");
    } finally {
      setLoadingImport(false);
      e.target.value = null; // Reset input
    }
  };

  const filteredBranches = branches.filter((b) =>
    Object.values(b)
      .join(" ")
      .toLowerCase()
      .includes(branchSearch.toLowerCase()),
  );

  // Slicing logic for pagination
  const paginatedBranches = useMemo(() => {
    return filteredBranches.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredBranches, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const lightPaper = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    color: "#000000",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  };

  const getTableHeight = () => {
    const toolbarHeight = 80;
    const paginationHeight = 80;
    const headerHeight = 100;
    const availableHeight =
      window.innerHeight - (toolbarHeight + paginationHeight + headerHeight);
    return `${Math.max(availableHeight, 300)}px`;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 1 }}>
      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, md: 0.5 },
        }}
      >
        {/* --- ACTION TOOLBAR --- */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            ...lightStyles.paper,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* RIGHT GROUP: SEARCH & REFRESH */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexGrow: isMobile ? 1 : 0, justifyContent: "flex-end" }}
          >
            <TextField
              placeholder="Search registry..."
              size="small"
              sx={{
                ...lightStyles.input,
                width: isMobile ? "100%" : "300px",
              }}
              value={branchSearch}
              onChange={(e) => setBranchSearch(e.target.value)}
            />

            <Typography
              variant="caption"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                border: "1px solid #e0e0e0",
                px: 1,
                py: 0.5,
                borderRadius: "4px",
                bgcolor: "rgba(255,214,0,0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "36px",
              }}
            >
              {filteredBranches.length}
            </Typography>
            <IconButton
              onClick={async () => {
                try {
                  fetchBranches();
                  showSnackbar("✅ Table updated", "success");
                } catch (err) {
                  showSnackbar("❌ Refresh failed", "error");
                }
              }}
              sx={{
                ...yellowButton,
                background: "transparent",
                border: "1px solid #FFD600",
                flexShrink: 0,
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>

        {/* --- Data Table --- */}
        <TableContainer
          sx={{
            maxHeight: getTableHeight(),
            overflowX: "auto",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { bgcolor: "#f5f5f5" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#FFD600",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar:horizontal": { height: "8px" },
            "&::-webkit-scrollbar-track:horizontal": { bgcolor: "#f5f5f5" },
            "&::-webkit-scrollbar-thumb:horizontal": {
              bgcolor: "#FFD600",
              borderRadius: "4px",
            },
          }}
        >
          {loading ? (
            <Box p={10} textAlign="center">
              <CircularProgress sx={{ color: "#FFD600" }} />
            </Box>
          ) : (
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 1400,
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    "Name",
                    "Username",
                    "Region",
                    "District",
                    "Status",
                    "Service #",
                    "P/Contact",
                    "S/Contact",
                  ].map((h) => (
                    <TableCell key={h} sx={lightStyles.tableHeader}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBranches.map((b, i) => (
                  <TableRow
                    key={b._id}
                    onClick={() => {
                      // Toggle: if clicked row is already selected, deselect it; otherwise select it
                      setSelectedRowId(selectedRowId === b._id ? null : b._id);
                    }}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedRowId === b._id ? "#ffb648f3" : "transparent",
                      "&:hover": {
                        bgcolor:
                          selectedRowId === b._id
                            ? "#ff790c"
                            : "rgba(255, 214, 0, 0.1)",
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <TableCell sx={lightStyles.tableCell}>{b.name}</TableCell>
                    <TableCell sx={lightStyles.tableCell}>
                      {b.username}
                    </TableCell>
                    <TableCell sx={lightStyles.tableCell}>{b.region}</TableCell>
                    <TableCell sx={lightStyles.tableCell}>
                      {b.district}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...lightStyles.tableCell,
                        color:
                          b.status === "Active"
                            ? "#00e676"
                            : b.status === "Intermittent"
                              ? "#ff9800"
                              : "#ff4444",
                      }}
                    >
                      {b.status}
                    </TableCell>
                    <TableCell sx={lightStyles.tableCell}>
                      {b.serviceNumber}
                    </TableCell>
                    <TableCell sx={lightStyles.tableCell}>
                      {b.primaryContact}
                    </TableCell>
                    <TableCell sx={lightStyles.tableCell}>
                      {b.secondaryContact}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200, 500, 1000, 3000]}
          component="div"
          count={filteredBranches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: "#000000",
            borderTop: "1px solid #e0e0e0",
            "& .MuiTablePagination-selectIcon": { color: "#FFD600" },
            "& .MuiIconButton-root": { color: "#FFD600" },
          }}
        />

        {/* --- DIALOG: Create/Edit --- */}
        <Dialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          TransitionComponent={Zoom}
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              ...lightStyles.dialogPaper,
              borderRadius: "12px",
              width: { xs: "95%", sm: "500px" },
              margin: "auto",
              overflowX: "hidden", // Prevents any micro-scrolling gaps
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#fafafa",
              color: "#000000",
              fontWeight: "900",
              borderBottom: "1px solid #eee",
              py: 1.5,
              px: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight="900">
              {editIndex !== null ? "MODIFY_NODE" : "INITIALIZE_NODE"}
            </Typography>

            {/* The Red Hover 'X' Button */}
            <IconButton
              onClick={() => setFormOpen(false)}
              size="small"
              sx={{
                color: "#999",
                transition: "0.2s",
                "&:hover": {
                  color: "#ff0000",
                  backgroundColor: "rgba(255, 0, 0, 0.05)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2, py: 2 }}>
            <Grid container spacing={2}>
              {/* sm={6} on a 500px wide paper means each box is exactly 250px. 
         This leaves 0px of "wasted" space on the right.
      */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.name}
                  onChange={handleBranchChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  name="username"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.username}
                  onChange={handleBranchChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Service #"
                  name="serviceNumber"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.serviceNumber}
                  onChange={handleBranchChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Gateway"
                  name="gateway"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.gateway}
                  onChange={handleBranchChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CPE IP"
                  name="cpeIP"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.cpeIP}
                  onChange={handleBranchChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primary Contact"
                  name="primaryContact"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.primaryContact}
                  onChange={handleBranchChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Secondary Contact"
                  name="secondaryContact"
                  fullWidth
                  size="small"
                  sx={lightStyles.input}
                  value={newBranch.secondaryContact}
                  onChange={handleBranchChange}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{ p: 2, borderTop: "1px solid #eee", bgcolor: "#fafafa" }}
          >
            <Button
              onClick={() => setFormOpen(false)}
              sx={{
                color: "#888",
                fontWeight: "bold",
                textTransform: "none",
                "&:hover": { color: "#444" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveBranch}
              sx={{
                ...lightStyles.actionBtn,
                px: 4,
                fontWeight: "bold",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              {editIndex !== null ? "Update Node" : "Initialize"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            sx={{
              bgcolor: "#ffffff",
              color: "#000000",
              border: "1px solid #FFD600",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default BranchCreation;
